import Text from '@/components/Text';
import Tooltip from 'components/tooltip/Tooltip';
import { orderBy } from 'lodash';
import { isMatchable } from 'services/DataStreamDefinitionService';
import { DataStreamListItem } from '../../../components/DataStreamListItem';
import { SearchFilter } from '../../../components/SearchFilter';
import { useFilterSearchItems } from '../../../hooks/useFilterSearchItems';
import type { TileEditorDataStream } from '../../../state/TileEditorState';

export type ConfigurationPresetListProps = {
    selectedDataStream: TileEditorDataStream | undefined;
    dataStreams: TileEditorDataStream[];
    onChange: (dataStream: TileEditorDataStream) => void;
};

const presetSearchKeys = [{ name: 'displayName', weight: 10 }];

export const ConfigurationPresetList = ({
    selectedDataStream,
    dataStreams,
    onChange
}: ConfigurationPresetListProps) => {
    const parentConfigurableDataStreamName =
        selectedDataStream?.template != null
            ? selectedDataStream.definition.name
            : selectedDataStream?.definition?.presetOf;

    const presets = orderBy(
        dataStreams.filter(
            (ds) =>
                ds.definition.presetOf === parentConfigurableDataStreamName &&
                ds.pluginId === selectedDataStream?.pluginId &&
                (isMatchable(ds) || ds.id === selectedDataStream?.id)
        ),
        'displayName'
    );

    const { searchItems, searchTerm, setSearchTerm } = useFilterSearchItems({
        items: presets ?? [],
        keys: presetSearchKeys
    });

    if (selectedDataStream?.template == null && selectedDataStream?.definition?.presetOf == null) {
        return <></>;
    }

    return (
        <>
            <div className='flex items-center justify-between gap-4 py-2 pl-6 pr-5 bg-componentTileBlock'>
                <Text.H4 className='text-textPrimary shrink-0'>Select a preset</Text.H4>
                <SearchFilter value={searchTerm} placeholder='Search' onChange={(e) => setSearchTerm(e.target.value)} />
            </div>

            <div
                className='flex flex-col min-h-0 gap-2 py-4 pl-6 pr-5 overflow-auto justify-items-stretch scrollbar-thin scrollbar-track-transparent scrollbar-thumb-statusUnknownPrimary'
                role='listbox'
            >
                {(searchItems ?? []).map((p) => {
                    return (
                        <Tooltip title={p.description ?? ''} disabled={!p.displayName}>
                            <DataStreamListItem
                                key={p.id}
                                isSelected={p.id === selectedDataStream.id}
                                onClick={() => onChange(p)}
                                data-testid='dataStreamSelectPreset'
                            >
                                <Text.Body className='pr-2 text-sm truncate'>{p.displayName}</Text.Body>
                            </DataStreamListItem>
                        </Tooltip>
                    );
                })}
            </div>
        </>
    );
};
